import { Glue42 } from "../glue";
import { ActivitiesLibConfigObject, LibConfigObject, InternalConfig, LayoutsLibConfigObject, AppManagerLibConfigObject } from "./types";
import Utils from "./utils";

interface InputModeObject {
    mode?: boolean | string;
    logger?: Glue42.LoggerConfig;
}

export default (options: Glue42.Config): InternalConfig => {

    // Possible options per lib (this is InputLibConfig type)
    // * false - library is disabled (e.g. config.appManager = false)
    // * true - library is enabled in running in default mode (e.g config.appManager = true).
    //          Exception is if default mode is false - in that case it runs default true mode.
    // * string - start in some mode (e.g. config.appManager = "full")
    // * object - allows further customization (e.g config.appManager = {mode: "full", someSetting: 42})
    //
    // Some libs support different running modes, some do not.
    //
    // Currently defaults are:
    // appManager: disabled
    // layouts: enabled in 'slim' mode
    // activities: enabled in 'trackMyTypeAndInitiatedFromMe' mode
    // windows: enabled (does not support modes)
    //
    // InputLibConfig is transformed to LibConfigObject

    type InputLibConfig = boolean | string | InputModeObject;

    /**
     * Transforms InputLibConfig (the config as specified from the user)
     * to LibConfigObject(internal library configuration)
     */
    function getLibConfig<T extends LibConfigObject<boolean | string>>(value: InputLibConfig, defaultMode: boolean | string, trueMode: boolean | string): T {

        // if value is false return undefined
        if (typeof value === "boolean" && !value) {
            return undefined;
        }

        // find the mode of the library
        const mode = getModeAsString(value, defaultMode, trueMode);

        if (typeof mode === "undefined") {
            return undefined;
        }

        // if object we will replace the mode because appManager = {mode: true, setting: 42}
        // should be turned into appManager = {mode: 'slim', setting: 42}
        if (typeof value === "object") {
            value.mode = mode;
            return value as T;
        }

        return {
            mode,
        } as T;
    }

    /**
     * Finds the mode based for a given library
     * 1. If object we call recursively using object.mode for value
     * 1. If the user hasn't specified anything we use the hard coded defaults
     * 3. If the value is false or it got defaulted to false, we return undefined
     * 4. If the value is true we return trueMode or defaultMode (if trueMode is undefined)
     */
    function getModeAsString(value: InputLibConfig, defaultMode: boolean | string, trueMode?: boolean | string): boolean | string {

        if (typeof value === "object") {
            // 1. if object
            return getModeAsString(value.mode, defaultMode, trueMode).toString();
        } else if (typeof value === "undefined") {
            // 2. if the user does not pass anything
            if (typeof defaultMode === "boolean" && !defaultMode) {
                // 3. if gets defaulted to false, the library should be off
                return undefined;
            } else if (typeof defaultMode === "boolean" && defaultMode) {
                // if the user passed in 'trueMode', then supposedly they expect
                // a default of 'true' to also be converted to 'trueMode'
                return typeof trueMode === "undefined" ? defaultMode : trueMode;
            } else if (typeof defaultMode === "undefined") {
                return undefined;
            } else {
                return defaultMode;
            }
        } else if (typeof value === "boolean") {
            // 4. if the user passes true, use trueMode or defaultMode
            if (value) {
                // if the user passes true, use trueMode or defaultMode
                return (typeof trueMode === "undefined") ? defaultMode : trueMode;
            } else {
                // 3. if the user passes false, the library should be off
                return undefined;
            }
        }

        return value;
    }

    const appDefaultMode = true;
    const appDefaultTrueMode = "startOnly";

    const activitiesDefaultMode = Utils.isNode() ? false : "trackMyTypeAndInitiatedFromMe";
    const activitiesTrueMode = "trackMyTypeAndInitiatedFromMe";

    const layoutsDefaultMode = "slim";
    const layoutsTrueMode = layoutsDefaultMode;

    const exposeGlue = typeof options.exposeGlue === "boolean" ? options.exposeGlue : true;

    return {
        layouts: getLibConfig<LayoutsLibConfigObject>(options.layouts, layoutsDefaultMode, layoutsTrueMode),
        activities: getLibConfig<ActivitiesLibConfigObject>(options.activities, activitiesDefaultMode, activitiesTrueMode),
        appManager: getLibConfig<AppManagerLibConfigObject>(options.appManager, appDefaultMode, appDefaultTrueMode),
        windows: getLibConfig<LibConfigObject<boolean>>(options.windows, true, true),
        channels: getLibConfig<LibConfigObject<boolean>>(options.channels, false, true),
        displays: getLibConfig<LibConfigObject<boolean>>(options.displays, true, true),
        exposeGlue
    };
};
